/**
 * Parameter Heatmap Mock
 *
 * Shows a grid of parameter combinations coloured by performance.
 * Illustrates the difference between a single sharp peak (overfitted)
 * and a broader stable region (robust).
 */

// 8×8 grid: rows = Stop Loss multiplier, cols = Lookback period
// Values represent simulated return %. One sharp peak vs a stable plateau.
const grid: number[][] = [
  //  12   14   16   18   20   22   24   26   ← Lookback
  [-4, -2, 1, 3, 2, -1, -3, -5], // SL 1.0
  [-1, 3, 6, 9, 7, 4, 1, -2], // SL 1.5
  [1, 5, 10, 14, 12, 8, 4, 0], // SL 2.0
  [2, 7, 13, 42, 15, 9, 5, 1], // SL 2.5  ← sharp peak
  [1, 6, 11, 15, 13, 8, 4, 0], // SL 3.0
  [-1, 4, 8, 11, 9, 6, 2, -1], // SL 3.5
  [-3, 1, 4, 7, 5, 3, 0, -3], // SL 4.0
  [-5, -2, 1, 3, 2, -1, -4, -6], // SL 4.5
];

const lookbacks = [12, 14, 16, 18, 20, 22, 24, 26];
const stops = ['1.0', '1.5', '2.0', '2.5', '3.0', '3.5', '4.0', '4.5'];

const maxVal = 42;

function cellColor(v: number): string {
  if (v >= 30) return 'rgba(239,68,68,0.85)';   // sharp peak — red/warning
  if (v >= 12) return 'rgba(34,197,94,0.55)';    // strong
  if (v >= 6) return 'rgba(34,197,94,0.30)';    // moderate
  if (v >= 1) return 'rgba(34,197,94,0.12)';    // weak positive
  return 'rgba(239,68,68,0.12)';                  // negative
}

function Cell({ value }: { value: number }) {
  const isPeak = value === maxVal;
  return (
    <div style={{
      width: '100%', aspectRatio: '1', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: cellColor(value),
      borderRadius: 3, fontSize: '0.5625rem',
      fontFamily: 'ui-monospace, monospace',
      fontWeight: isPeak ? 700 : 400,
      color: isPeak ? '#ef4444' : value >= 6 ? '#d1d5db' : '#6b7280',
      border: isPeak ? '1px solid rgba(239,68,68,0.5)' : '1px solid transparent',
    }}>
      {value > 0 ? `+${value}` : value}%
    </div>
  );
}

export default function ParameterHeatmapMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Parameter Sensitivity Heatmap
      </div>
      <div className="platform-mock-body">
        {/* Column labels */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'auto repeat(8, 1fr)',
          gap: 3, marginBottom: 3,
        }}>
          <div style={{ width: 40 }} />
          {lookbacks.map(lb => (
            <div key={lb} style={{
              textAlign: 'center', fontSize: '0.5625rem', color: '#6b7280',
              fontFamily: 'ui-monospace, monospace',
            }}>{lb}</div>
          ))}
        </div>

        {/* Grid rows */}
        {grid.map((row, ri) => (
          <div key={ri} style={{
            display: 'grid',
            gridTemplateColumns: 'auto repeat(8, 1fr)',
            gap: 3, marginBottom: 3,
          }}>
            <div style={{
              width: 40, display: 'flex', alignItems: 'center',
              fontSize: '0.5625rem', color: '#6b7280',
              fontFamily: 'ui-monospace, monospace',
            }}>{stops[ri]}x</div>
            {row.map((val, ci) => (
              <Cell key={ci} value={val} />
            ))}
          </div>
        ))}

        {/* Axis labels */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: '0.5625rem', color: '#4b5563', marginTop: '0.5rem',
        }}>
          <span>Y-axis: Stop Loss multiplier</span>
          <span>X-axis: Lookback period</span>
        </div>

        {/* Interpretation */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem',
          marginTop: '0.75rem',
        }}>
          <div style={{
            padding: '0.5rem 0.75rem', borderRadius: 6,
            background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
          }}>
            <div style={{ fontSize: '0.5625rem', fontWeight: 600, color: '#ef4444', marginBottom: '0.15rem' }}>
              Sharp Peak (+42%)
            </div>
            <div style={{ fontSize: '0.5625rem', color: '#9ca3af' }}>
              One setting vastly outperforms neighbours. Likely overfitted to a specific historical pattern.
            </div>
          </div>
          <div style={{
            padding: '0.5rem 0.75rem', borderRadius: 6,
            background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)',
          }}>
            <div style={{ fontSize: '0.5625rem', fontWeight: 600, color: '#22c55e', marginBottom: '0.15rem' }}>
              Stable Region (+9% to +15%)
            </div>
            <div style={{ fontSize: '0.5625rem', color: '#9ca3af' }}>
              Multiple nearby settings perform consistently. A much healthier sign of real edge.
            </div>
          </div>
        </div>
      </div>
      <div className="platform-mock-caption">
        If only one exact parameter setting works, the edge is probably noise. Look for stable regions.
      </div>
    </div>
  );
}
